import { useRequest } from "redux-query-react";
import { useSelector } from "react-redux";
import { isEqual } from "lodash";
import { objectToHash } from "common/object-helper";
import { fundSeriesFromJSON, fundSeriesListItemFromJSON } from "./types";
import { getConfig } from "plugins/config-loader";
import { normalizeBaseUrl } from "common/string-helper";
const getApiUrl = () => {
    const baseUrl = normalizeBaseUrl(getConfig("REACT_APP_IDD_BACKEND_BASE_URL") ?? "");
    return `${baseUrl}/fundSeries`;
};
export const getFundSeriesByIdRaw = (requestParameters, requestConfig = {}) => {
    // Return a no-op config if no ID is provided (e.g., on list pages)
    if (!requestParameters.id) {
        // Dummy URL that won't be executed due to force: false
        return {
            force: false,
            queryKey: "GetFundSeriesById-no-id",
            transform: () => ({}),
            url: "about:blank"
        };
    }
    const queryParameters = null;
    const queryString = "";
    const headerParameters = {};
    const { meta = {} } = requestConfig;
    const config = {
        body: queryParameters,
        force: requestConfig.force,
        meta,
        options: requestConfig.options || {
            headers: headerParameters,
            method: "GET"
        },
        transform: requestConfig.transform ?? ((body) => {
            const objResponse = fundSeriesFromJSON(body);
            let key = "";
            if (requestParameters.id) {
                key = `${key}${requestParameters.id}`;
            }
            return { FundSeries: { [key]: objResponse } };
        }),
        update: requestConfig.update || {
            FundSeries: (prevValue, transformedValue) => ({ ...prevValue, ...transformedValue })
        },
        url: `${getApiUrl()}/{id}`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))) + queryString
    };
    config.queryKey = requestConfig.queryKey || `GetFundSeriesById-${objectToHash(requestParameters)}`;
    return config;
};
export const useFundSeriesByIdSelector = (id) => {
    const key = id ?? "";
    return useSelector((state) => state.entities.FundSeries?.[key] ?? {}, isEqual);
};
export const useGetFundSeriesById = (requestParameters, customQueryConfig) => {
    const [querystate, forceRefresh] = useRequest(getFundSeriesByIdRaw(requestParameters, customQueryConfig));
    return [
        useFundSeriesByIdSelector(requestParameters.id),
        querystate,
        forceRefresh
    ];
};
export const getFundSeriesById = (requestParameters, requestConfig) => getFundSeriesByIdRaw(requestParameters, requestConfig);
export const getAllFundSeriesRaw = (requestConfig = {}) => {
    const queryParameters = null;
    const queryString = "";
    const headerParameters = {};
    const { meta = {} } = requestConfig;
    const config = {
        body: queryParameters,
        force: requestConfig.force,
        meta,
        options: requestConfig.options || {
            headers: headerParameters,
            method: "GET"
        },
        transform: requestConfig.transform ?? ((body) => {
            // Handle response with items array
            const items = body.items ?? [];
            const objResponse = items.map(fundSeriesListItemFromJSON);
            return { AllFundSeries: objResponse };
        }),
        update: requestConfig.update || {
            AllFundSeries: (prevValue, transformedValue) => transformedValue
        },
        url: `${getApiUrl()}${queryString}`
    };
    config.queryKey = requestConfig.queryKey || "GetAllFundSeries";
    return config;
};
export const deleteFundSeriesRaw = (requestParameters, requestConfig = {}) => {
    const queryString = "";
    const headerParameters = {};
    const { meta = {} } = requestConfig;
    const config = {
        body: null,
        force: requestConfig.force,
        meta,
        options: requestConfig.options || {
            headers: headerParameters,
            method: "DELETE"
        },
        transform: requestConfig.transform ?? (() => ({})),
        update: requestConfig.update || {
            AllFundSeries: (prevValue = []) => prevValue.filter((item) => item.id !== requestParameters.id)
        },
        url: `${getApiUrl()}/{id}`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))) + queryString
    };
    config.queryKey = requestConfig.queryKey || `DeleteFundSeries-${objectToHash(requestParameters)}`;
    return config;
};
export const deleteFundSeries = (requestParameters, requestConfig) => deleteFundSeriesRaw(requestParameters, requestConfig);
export const bulkDeleteFundSeriesRaw = (requestParameters, requestConfig = {}) => {
    const queryString = "";
    const headerParameters = {};
    const { meta = {} } = requestConfig;
    const config = {
        body: requestParameters,
        force: requestConfig.force,
        meta,
        options: requestConfig.options || {
            headers: headerParameters,
            method: "POST"
        },
        transform: requestConfig.transform ?? (() => ({})),
        update: requestConfig.update || {
            AllFundSeries: (prevValue = []) => prevValue.filter((item) => !requestParameters.ids.includes(item.id))
        },
        url: `${getApiUrl()}:bulkDelete${queryString}`
    };
    config.queryKey = requestConfig.queryKey || `BulkDeleteFundSeries-${objectToHash(requestParameters)}`;
    return config;
};
export const bulkDeleteFundSeries = (requestParameters, requestConfig) => bulkDeleteFundSeriesRaw(requestParameters, requestConfig);
export const useAllFundSeriesSelector = () => useSelector((state) => state.entities.AllFundSeries ?? [], isEqual);
export const useGetAllFundSeries = (customQueryConfig) => {
    const [querystate, forceRefresh] = useRequest(getAllFundSeriesRaw(customQueryConfig));
    return [
        useAllFundSeriesSelector(),
        querystate,
        forceRefresh
    ];
};
export const getAllFundSeries = (requestConfig) => getAllFundSeriesRaw(requestConfig);
